import { FormattedMessage } from 'react-intl';
import { Localized } from '@synergy/components';
import { MouseEvent, useEffect, useState } from 'react';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { SupplierMapping, Unit } from '@synergy/models';
import { defaultPageSize } from '@synergy/config';
import { padZeroLeft } from '@synergy/utils';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as UnitActions from './store/supplier-unit-mapping-actions';
import { MappedValues } from '../../../../../common/components/mapped-values';
import { MappingEditModal } from '../../../../../common/components/mapping-edit-modal/mapping-edit-modal';
import { bestLocaleSelector } from '../../../../../common/selectors/locale-selectors';
import { supplierUnitMappingSelector } from './store/supplier-unit-mapping-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export const SupplierUnitMapping = () => {
    const locale = useAppSelector(bestLocaleSelector);
    const { id: supplierIdParam } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();

    const units = useAppSelector(supplierUnitMappingSelector);
    const dispatch = useThunkDispatch();

    const [selectedMapping, setSelectedMapping] = useState<SupplierMapping<Unit>>();

    useEffect(() => {
        if (supplierIdParam) {
            dispatch(UnitActions.fetchSupplierUnitMapping(+supplierIdParam, searchParams));
        }
    }, [dispatch, supplierIdParam, searchParams]);

    const handleMappingEdit = (mapping: string[]): void => {
        if (supplierIdParam && selectedMapping) {
            const supplierMapping: SupplierMapping<Unit> = {
                ...selectedMapping,
                supplierMapping: mapping,
            };
            dispatch(UnitActions.updateSupplierUnitMapping(+supplierIdParam, supplierMapping));
        }
        setSelectedMapping(undefined);
    };

    const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, page: number) => {
        if (supplierIdParam) {
            searchParams.set('page', page.toString());
            dispatch(UnitActions.fetchSupplierUnitMapping(+supplierIdParam, searchParams));
        }
    };

    const handleChangeLimit = (limit: number) => {
        if (supplierIdParam) {
            searchParams.set('limit', (limit || defaultPageSize).toString());
            searchParams.set('page', '1');
            dispatch(UnitActions.fetchSupplierUnitMapping(+supplierIdParam, searchParams));
        }
    };

    const sortOrder: 'asc' | 'desc' = 'desc' === searchParams.get('order') ? 'desc' : 'asc';

    const handleSort = (order: 'asc' | 'desc', sort: string) => {
        if (supplierIdParam) {
            searchParams.set('sort', sort);
            searchParams.set('order', order);
            searchParams.set('locale', locale);
            dispatch(UnitActions.fetchSupplierUnitMapping(+supplierIdParam, searchParams));
        }
    };

    const columns: PaloColumn<SupplierMapping<Unit>>[] = [
        {
            key: 'translation',
            type: 'name',
            sortKey: 'translation',
            head: <FormattedMessage id="app.table.column.name" />,
            cell: unit => (
                <>
                    <Localized value={unit.translations} /> ({padZeroLeft(unit.id, 3)})
                </>
            ),
        },
        {
            key: 'defaultMapping',
            type: 'name',
            wrap: true,
            head: <FormattedMessage id="app.table.column.mapping.default" />,
            cell: unit => <MappedValues values={unit.defaultMapping} />,
        },
        {
            key: 'supplierMapping',
            type: 'name',
            wrap: true,
            head: <FormattedMessage id="app.table.column.mapping.own" />,
            cell: unit => <MappedValues values={unit.supplierMapping} />,
        },
    ];

    return (
        <>
            <PaloListView<SupplierMapping<Unit>, number>
                embedded={true}
                columns={columns}
                items={units.items}
                getRowId={unit => unit.id}
                emptyLabel={<FormattedMessage id="app.table.column.mapping.own" />}
                sort={{ key: searchParams.get('sort') ?? 'translation', order: sortOrder }}
                onSortChange={(key, order) => handleSort(order, key)}
                rowActions={unit => [
                    { label: <FormattedMessage id="app.common.edit" />, onClick: () => setSelectedMapping(unit) },
                ]}
                pagination={{
                    count: units.total,
                    limit: units.limit,
                    page: units.page,
                    onPageChange: page => handleChangePage(null, page),
                    onLimitChange: limit => handleChangeLimit(limit),
                }}
            />
            {selectedMapping && (
                <MappingEditModal
                    visible={!!selectedMapping}
                    mapping={selectedMapping.supplierMapping}
                    onClose={() => setSelectedMapping(undefined)}
                    onSubmit={handleMappingEdit}
                />
            )}
        </>
    );
};
